import { useEffect } from "react";

/**
 * Returns the UI label for the meta key on the current platform.
 * Mac uses ⌘ (Cmd); every other platform uses Ctrl.
 *
 * SSR-safe — defaults to "Ctrl" when ``navigator`` isn't available.
 */
export function getMetaKeyLabel(): "⌘" | "Ctrl" {
  if (typeof navigator === "undefined") return "Ctrl";
  const platform = navigator.platform || "";
  const ua = navigator.userAgent || "";
  return /Mac|iPhone|iPad|iPod/i.test(platform + " " + ua) ? "⌘" : "Ctrl";
}

/**
 * Render a combo with the platform-aware meta key — e.g.
 *   formatShortcut("K") → "⌘K" on Mac, "Ctrl+K" elsewhere.
 */
export function formatShortcut(key: string): string {
  const meta = getMetaKeyLabel();
  return meta === "⌘" ? `${meta}${key}` : `${meta}+${key}`;
}

export interface ShortcutHandler {
  /** The non-modifier key (``"s"``, ``"k"``, ``"Delete"``, etc.). Case-insensitive. */
  key: string;
  /** When true the shortcut requires Cmd (Mac) / Ctrl (other) to fire. */
  meta?: boolean;
  shift?: boolean;
  alt?: boolean;
  /** Callback fired when the combo matches. Receives the original event. */
  run: (event: KeyboardEvent) => void;
  /** Prevent default + stop propagation after matching. Defaults to true. */
  preventDefault?: boolean;
}

const TEXT_TAGS = new Set(["INPUT", "TEXTAREA", "SELECT"]);

/**
 * Register a list of keyboard shortcuts for the current page.
 *
 * The runtime treats Cmd on macOS and Ctrl everywhere else as the same
 * "meta" bit so shortcuts like ``Cmd+S`` / ``Ctrl+S`` just work.
 *
 * Shortcuts DO fire while the user is typing in inputs / textareas —
 * that's intentional for Save (``Cmd+S``) and Preview (``Cmd+K``). Plain
 * single-key shortcuts (``Delete``) bail out when the target is a
 * text field to avoid deleting characters from unrelated inputs.
 */
export function useKeyboardShortcuts(
  handlers: ReadonlyArray<ShortcutHandler>,
  options: { enabled?: boolean } = {},
): void {
  const enabled = options.enabled ?? true;

  useEffect(() => {
    if (!enabled) return;
    const onKey = (event: KeyboardEvent): void => {
      const metaDown = event.metaKey || event.ctrlKey;
      const target = event.target as HTMLElement | null;
      const inTextField =
        target && (TEXT_TAGS.has(target.tagName) || target.isContentEditable);

      for (const h of handlers) {
        if (h.key.toLowerCase() !== event.key.toLowerCase()) continue;
        if (Boolean(h.meta) !== metaDown) continue;
        if ((h.shift ?? false) !== event.shiftKey) continue;
        if ((h.alt ?? false) !== event.altKey) continue;
        // Skip plain keys while the user is typing in a text field.
        if (!h.meta && inTextField) continue;
        if (h.preventDefault !== false) {
          event.preventDefault();
          event.stopPropagation();
        }
        h.run(event);
        return;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [handlers, enabled]);
}
